import { HttpContextToken, HttpResponse, HTTP_INTERCEPTORS } from '@angular/common/http';
import * as i0 from '@angular/core';
import { Injectable, NgModule } from '@angular/core';
import { of } from 'rxjs';
import { tap, share } from 'rxjs/operators';
import * as i1 from '@vcomply/authorizer';

var VComplyCache;
(function (VComplyCache) {
    let TTL;
    (function (TTL) {
        TTL[TTL["Zero"] = 0] = "Zero";
        TTL[TTL["OneMinute"] = 60000] = "OneMinute";
        TTL[TTL["TwoMinute"] = 120000] = "TwoMinute";
        TTL[TTL["FiveMinute"] = 300000] = "FiveMinute";
        TTL[TTL["TenMinutes"] = 600000] = "TenMinutes";
        TTL[TTL["OneHour"] = 3600000] = "OneHour";
        TTL[TTL["OneDay"] = 86400000] = "OneDay";
    })(TTL = VComplyCache.TTL || (VComplyCache.TTL = {}));
    let ApiLevel;
    (function (ApiLevel) {
        ApiLevel["Public"] = "public";
        ApiLevel["User"] = "user";
    })(ApiLevel = VComplyCache.ApiLevel || (VComplyCache.ApiLevel = {}));
})(VComplyCache || (VComplyCache = {}));

const CACHE_ENABLED = {
    GLOBAL: true,
};
const CACHE_HOLDER_KEY = '_vcc';
const CACHE_ALLOWED_API_METHODS = ['GET'];
const CACHE_ALLOWED_APIS = [
    {
        enabled: true,
        key: 'features',
        level: VComplyCache.ApiLevel.Public,
        expires: VComplyCache.TTL.FiveMinute,
    },
    {
        enabled: true,
        key: 'globals?mode=bucket',
        level: VComplyCache.ApiLevel.Public,
        expires: 30 * VComplyCache.TTL.OneDay,
    },
    {
        enabled: true,
        key: 'organization/?mode=orgDetails',
        level: VComplyCache.ApiLevel.Public,
        expires: VComplyCache.TTL.FiveMinute,
    },
    {
        enabled: true,
        key: 'organization/?mode=subscriptionList',
        level: VComplyCache.ApiLevel.Public,
        expires: VComplyCache.TTL.FiveMinute,
    },
    {
        enabled: true,
        key: 'organization/?mode=users',
        level: VComplyCache.ApiLevel.Public,
        expires: VComplyCache.TTL.FiveMinute,
    },
    // 'organization_post/?mode=GenerateDynamicQuarter': {
    //   level: VComplyCache.ApiLevel.Organisation,
    //   cache: true,
    //   expires: 30 * VComplyCache.TTL.OneDay,
    // },
];

const SKIP_CACHE = new HttpContextToken(() => false);
class CacheService {
    constructor(authorizerService) {
        this.authorizerService = authorizerService;
        this.cache = {};
        this.loadCacheFromStore();
    }
    isCacheEnabled(req) {
        if (req.context.get(SKIP_CACHE) === true ||
            CACHE_ENABLED.GLOBAL === false) {
            return false;
        }
        return true;
    }
    // get item by key & level from cache store
    getItem(key, level = VComplyCache.ApiLevel.Public) {
        // Get the item based on the key and level
        const cachedItem = this.getCachedItem(key, level);
        if (!cachedItem) {
            return null;
        }
        // Expiry check
        const expiresAt = cachedItem.expiresAt;
        const now = Date.now();
        if (now > expiresAt) {
            return null;
        }
        return cachedItem.value;
    }
    // set item by key and level at cache store
    setItem(key, value, ttl = VComplyCache.TTL.Zero, level = VComplyCache.ApiLevel.Public) {
        // Sets the item based on level
        switch (level) {
            case VComplyCache.ApiLevel.Public:
                this.setPublicItem(key, value, ttl);
                break;
            case VComplyCache.ApiLevel.User:
                this.setUserItem(key, value, ttl);
                break;
        }
        // Sync with browser store
        this.updateCacheToStore();
    }
    // clears the value by key and level
    clearByKey(key, level = VComplyCache.ApiLevel.Public) {
        this.setItem(key, null, VComplyCache.TTL.Zero, level);
        this.updateCacheToStore();
    }
    clearByUserId(userId) {
        const level = VComplyCache.ApiLevel.User;
        delete this.cache[level]?.[userId];
        this.updateCacheToStore();
    }
    clearByLevel(level = VComplyCache.ApiLevel.Public) {
        delete this.cache[level];
        this.updateCacheToStore();
    }
    clearCache() {
        this.cache = {};
        this.updateCacheToStore();
    }
    getApiCacheConfiguration(url) {
        return CACHE_ALLOWED_APIS.find((config) => config.enabled && url.includes(config.key));
    }
    /**
     * This returns the cache key by stripping off the host/domain
     */
    getCacheKeyFromUrl(requestUrl) {
        const url = new URL(requestUrl);
        const origin = url.origin;
        return requestUrl.replace(origin, '');
    }
    /**
     * Private methods
     */
    loadCacheFromStore() {
        try {
            const data = localStorage.getItem(CACHE_HOLDER_KEY) || '{}';
            this.cache = JSON.parse(data);
        }
        catch { }
    }
    updateCacheToStore() {
        try {
            const data = JSON.stringify(this.cache);
            localStorage.setItem(CACHE_HOLDER_KEY, data);
        }
        catch {
            localStorage.removeItem(CACHE_HOLDER_KEY);
        }
    }
    getCachedItem(key, level) {
        const levelData = this.cache[level];
        switch (level) {
            case VComplyCache.ApiLevel.Public:
                return levelData?.[key];
            case VComplyCache.ApiLevel.User: {
                const userId = this.authorizerService.getUserId() || '';
                return levelData?.[userId]?.[key];
            }
        }
    }
    setPublicItem(key, value, ttl) {
        const level = VComplyCache.ApiLevel.Public;
        this.cache[level] = {
            ...this.cache[level],
            [key]: {
                value,
                expiresAt: Date.now() + ttl,
            },
        };
    }
    setUserItem(key, value, ttl) {
        const level = VComplyCache.ApiLevel.User;
        const userId = this.authorizerService.getUserId() || '';
        if (userId) {
            this.cache[level] = {
                ...this.cache[level],
                [userId]: {
                    ...this.cache[level]?.[userId],
                    [key]: {
                        value,
                        expiresAt: Date.now() + ttl,
                    },
                },
            };
        }
    }
}
CacheService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: CacheService, deps: [{ token: i1.AuthorizerService }], target: i0.ɵɵFactoryTarget.Injectable });
CacheService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: CacheService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: CacheService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.AuthorizerService }]; } });

class CacheInterceptor {
    constructor(cacheService) {
        this.cacheService = cacheService;
    }
    intercept(request, next) {
        // Check whether the flags are enabled
        if (!this.isCacheEnabled(request) || !this.isApiEnabled(request)) {
            return next.handle(request);
        }
        // Get the API level configuration
        const config = this.cacheService.getApiCacheConfiguration(request?.url);
        if (!config) {
            return next.handle(request);
        }
        // Check the cache data
        const key = this.cacheService.getCacheKeyFromUrl(request?.url);
        const { level, expires } = config;
        const cacheValue = this.cacheService.getItem(key, level);
        if (cacheValue) {
            return of(new HttpResponse(cacheValue));
        }
        // Cache miss
        return next.handle(request).pipe(tap({
            next: (stateEvent) => {
                if (stateEvent instanceof HttpResponse) {
                    this.cacheService.setItem(key, stateEvent.clone(), expires, level);
                }
            },
        }), share());
    }
    isCacheEnabled(request) {
        return this.cacheService.isCacheEnabled(request);
    }
    isApiEnabled(request) {
        return CACHE_ALLOWED_API_METHODS.includes(request?.method.toUpperCase());
    }
}
CacheInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: CacheInterceptor, deps: [{ token: CacheService }], target: i0.ɵɵFactoryTarget.Injectable });
CacheInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: CacheInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: CacheInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: CacheService }]; } });

class CacheModule {
    static forRoot() {
        return {
            ngModule: CacheModule,
        };
    }
}
CacheModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: CacheModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CacheModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: CacheModule });
CacheModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: CacheModule, providers: [
        { provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true },
    ], imports: [[]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: CacheModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [],
                    exports: [],
                    providers: [
                        { provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true },
                    ],
                }]
        }] });

/*
 * Public API Surface of vcomply-cache
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CacheInterceptor, CacheModule, CacheService, SKIP_CACHE };
